// /frontend/src/components/PlatformDropdown.js  (v2.0 – modernized UI)
import React, { useState, useRef, useEffect } from 'react';
import { getPlatformIcon } from '../pages/dashboard/accounts/helpers/platformIcons';

const BRAND_COLORS = {
  facebook  : '#1877F2',
  instagram : '#E4405F',
  twitter   : '#1DA1F2',
  x         : '#000000',
  linkedin  : '#0A66C2',
  youtube   : '#FF0000',
  tiktok    : '#000000',
  pinterest : '#E60023',
};

const PLATFORM_LABELS = {
  facebook  : 'Facebook',
  instagram : 'Instagram',
  twitter   : 'Twitter / X',
  x         : 'Twitter / X',
  linkedin  : 'LinkedIn',
  youtube   : 'YouTube',
  tiktok    : 'TikTok',
  pinterest : 'Pinterest',
};

function getBrandColor(platform) {
  return BRAND_COLORS[platform] || '#6b7280';
}

function PlatformDropdown({ uniquePlatforms, selectedPlatform, onChange }) {
  const [open, setOpen]       = useState(false);
  const [hovered, setHovered] = useState(null);
  const dropdownRef           = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAll = selectedPlatform === 'all';

  /* Trigger button content */
  const triggerContent = isAll ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {uniquePlatforms.slice(0, 5).map(p => (
        <i key={p} className={getPlatformIcon(p)} style={{ fontSize: 14, color: getBrandColor(p) }} />
      ))}
      {uniquePlatforms.length > 5 && (
        <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>+{uniquePlatforms.length - 5}</span>
      )}
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <i className={getPlatformIcon(selectedPlatform)} style={{ fontSize: 14, color: getBrandColor(selectedPlatform) }} />
      <span>{PLATFORM_LABELS[selectedPlatform] || selectedPlatform}</span>
    </div>
  );

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>

      {/* Trigger button */}
      <button
        type='button'
        onClick={() => setOpen(!open)}
        style={{
          display      : 'flex',
          alignItems   : 'center',
          gap          : 7,
          padding      : '7px 12px',
          borderRadius : 8,
          border       : `1.5px solid ${open ? '#2563eb' : '#e5e7eb'}`,
          background   : open ? '#eff6ff' : '#ffffff',
          cursor       : 'pointer',
          fontSize     : 13,
          fontWeight   : 600,
          color        : open ? '#2563eb' : '#374151',
          transition   : 'all 0.15s',
          whiteSpace   : 'nowrap',
          boxShadow    : '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        {triggerContent}
        <i
          className={`fas fa-chevron-${open ? 'up' : 'down'}`}
          style={{ fontSize: 10, color: '#9ca3af', marginLeft: 2 }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position    : 'absolute',
          top         : 'calc(100% + 6px)',
          left        : 0,
          background  : '#ffffff',
          border      : '1px solid #e5e7eb',
          borderRadius: 10,
          minWidth    : 200,
          zIndex      : 1000,
          padding     : 4,
          boxShadow   : '0 8px 24px rgba(0,0,0,0.1)',
        }}>

          {/* All platforms option */}
          <div
            onClick={() => { onChange('all'); setOpen(false); }}
            onMouseEnter={() => setHovered('all')}
            onMouseLeave={() => setHovered(null)}
            style={{
              display      : 'flex',
              alignItems   : 'center',
              gap          : 10,
              padding      : '8px 12px',
              borderRadius : 7,
              cursor       : 'pointer',
              background   : hovered === 'all'
                ? '#f8fafc'
                : isAll
                  ? '#eff6ff'
                  : 'transparent',
              marginBottom : 2,
            }}
          >
            {/* Mini icon row */}
            <div style={{ display: 'flex', gap: 3, alignItems: 'center', minWidth: 70 }}>
              {uniquePlatforms.slice(0, 4).map(p => (
                <i key={p} className={getPlatformIcon(p)} style={{ fontSize: 12, color: getBrandColor(p) }} />
              ))}
            </div>
            <span style={{
              fontSize  : 13,
              fontWeight: isAll ? 700 : 500,
              color     : isAll ? '#2563eb' : '#374151',
            }}>
              All Platforms
            </span>
            {isAll && <i className='fas fa-check' style={{ fontSize: 10, color: '#2563eb', marginLeft: 'auto' }} />}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: '#f3f4f6', margin: '4px 8px' }} />

          {/* Individual platforms */}
          {uniquePlatforms.map(p => {
            const isSelected = selectedPlatform === p;
            const color      = getBrandColor(p);
            return (
              <div
                key={p}
                onClick={() => { onChange(p); setOpen(false); }}
                onMouseEnter={() => setHovered(p)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  display      : 'flex',
                  alignItems   : 'center',
                  gap          : 10,
                  padding      : '8px 12px',
                  borderRadius : 7,
                  cursor       : 'pointer',
                  background   : hovered === p
                    ? '#f8fafc'
                    : isSelected
                      ? '#f0f9ff'
                      : 'transparent',
                  transition   : 'background 0.1s',
                }}
              >
                <div style={{
                  width        : 28,
                  height       : 28,
                  borderRadius : '50%',
                  background   : `${color}15`,
                  display      : 'flex',
                  alignItems   : 'center',
                  justifyContent: 'center',
                  flexShrink   : 0,
                }}>
                  <i className={getPlatformIcon(p)} style={{ fontSize: 13, color }} />
                </div>
                <span style={{
                  fontSize  : 13,
                  fontWeight: isSelected ? 700 : 500,
                  color     : isSelected ? color : '#374151',
                  textTransform: 'capitalize',
                }}>
                  {PLATFORM_LABELS[p] || p}
                </span>
                {isSelected && (
                  <i className='fas fa-check' style={{ fontSize: 10, color, marginLeft: 'auto' }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PlatformDropdown;